import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scissors, Mail, Lock, User2, AlertTriangle, LogIn } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || JSON.stringify(detail);
}

function googleSignup() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailTaken, setEmailTaken] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailTaken(false);
    setBusy(true);
    try {
      await register(email, password, name);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Email already exists — show a clear "sign in instead" path
        setEmailTaken(true);
      } else if (!err.response) {
        // No server response — network failure on the client side.
        // The request may have actually been processed by the server.
        setError(
          "Network issue. Your account may have already been created — try signing in with the same email.",
        );
      } else {
        setError(formatError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 animate-fade-in">
      <div className="w-full max-w-md cm-card p-8 md:p-10">
        <div className="flex items-center gap-2 mb-2">
          <Scissors className="w-5 h-5 text-copper" />
          <span className="cm-heading text-xl">CREATE ACCOUNT</span>
        </div>
        <p className="text-sm text-gray-400 mb-8">5 free scans every 12 hours — on every account.</p>

        <button
          onClick={googleSignup}
          data-testid="google-signup-button"
          className="cm-btn-secondary w-full flex items-center justify-center gap-3 mb-5"
        >
          <span className="font-bold text-base">G</span>
          <span>Continue with Google</span>
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-ink-700" />
          <span className="text-[10px] tracking-widest uppercase text-gray-500">or</span>
          <div className="flex-1 h-px bg-ink-700" />
        </div>

        <form onSubmit={submit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="register-name" className="cm-label block mb-2">Name</label>
            <div className="relative">
              <User2 className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="register-name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="cm-input pl-10"
                placeholder="Your name"
                data-testid="register-name-input"
              />
            </div>
          </div>
          <div>
            <label htmlFor="register-email" className="cm-label block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="register-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cm-input pl-10"
                placeholder="you@domain.com"
                data-testid="register-email-input"
              />
            </div>
          </div>
          <div>
            <label htmlFor="register-password" className="cm-label block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="register-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="cm-input pl-10"
                placeholder="6+ characters"
                aria-describedby="register-password-hint"
                data-testid="register-password-input"
              />
            </div>
            <p id="register-password-hint" className="sr-only">Password must be at least 6 characters.</p>
          </div>
          {emailTaken && (
            <div
              className="cm-card p-4 border-copper/50 space-y-3"
              data-testid="register-email-taken-banner"
            >
              <div className="flex items-start gap-2 text-sm text-copper">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>
                  An account with <b>{email}</b> already exists. Maybe a previous attempt went
                  through — try signing in instead.
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  navigate("/login", { state: { prefillEmail: email } })
                }
                className="cm-btn-primary w-full flex items-center justify-center gap-2"
                data-testid="register-go-to-login-button"
              >
                <LogIn className="w-4 h-4" /> Sign in with this email
              </button>
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400" data-testid="register-error">
              <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="cm-btn-primary w-full" data-testid="register-submit-button">
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-sm text-gray-400 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-copper hover:underline" data-testid="register-to-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
